"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import PrimaryButton from "@/components/utils/button/PrimaryButton";
import SecondaryButton from "@/components/utils/button/SecondaryButton";
import type { ApiCreateOrUpdateProfileCicStatement, CicStatement } from "@/entities/IProfile";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import { STATEMENT_GROUP, STATEMENT_TYPE } from "@/helpers/Types";
import { commonApiPost } from "@/services/api/common-api";
import { useMutation } from "@tanstack/react-query";
import { AnimatePresence } from "framer-motion";
import { useContext, useEffect, useRef, useState } from "react";
import { useKeyPressEvent } from "react-use";
import UserPageHeaderAboutEditError from "./UserPageHeaderAboutEditError";
export default function UserPageHeaderAboutEdit(t0) {
  const $ = _c(60);
  const {
    profile,
    statement,
    onClose
  } = t0;
  const {
    onProfileStatementAdd
  } = useContext(ReactQueryWrapperContext);
  const {
    requestAuth,
    setToast
  } = useContext(AuthContext);
  useKeyPressEvent("Escape", onClose);
  const inputRef = useRef(null);
  const [errorMsg, setErrorMsg] = useState(null);
  let t1;
  if ($[0] !== profile.handle || $[1] !== profile.primary_wallet || $[2] !== profile.query) {
    t1 = [profile.query, profile.handle, profile.primary_wallet].map(_temp).find(_temp2) ?? "";
    $[0] = profile.handle;
    $[1] = profile.primary_wallet;
    $[2] = profile.query;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const profileStatementTarget = t1;
  let t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (inputRef.current) {
        inputRef.current.focus();
        const {
          value: value_1
        } = inputRef.current;
        inputRef.current.setSelectionRange(value_1.length, value_1.length);
      }
    };
    t3 = [];
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  const [value_2, setValue] = useState(statement?.statement_value ?? "");
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = event => {
      setErrorMsg(null);
      const newValue = event.target.value;
      if (newValue.length > 500) {
        setValue(newValue.substring(0, 500));
        return;
      }
      setValue(newValue);
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleInputChange = t4;
  const [loading, setLoading] = useState(false);
  let t5;
  if ($[7] !== loading || $[8] !== profileStatementTarget.length || $[9] !== statement?.statement_value || $[10] !== value_2) {
    t5 = profileStatementTarget.length === 0 || value_2.trim().length === 0 || statement?.statement_value === value_2 || loading;
    $[7] = loading;
    $[8] = profileStatementTarget.length;
    $[9] = statement?.statement_value;
    $[10] = value_2;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const isDisabled = t5;
  let t6;
  if ($[12] !== profileStatementTarget) {
    t6 = async statementValue => {
      setLoading(true);
      setErrorMsg(null);
      return await commonApiPost({
        endpoint: `profiles/${profileStatementTarget}/cic/statements`,
        body: {
          statement_group: STATEMENT_GROUP.GENERAL,
          statement_type: STATEMENT_TYPE.BIO,
          statement_comment: null,
          statement_value: statementValue
        }
      });
    };
    $[12] = profileStatementTarget;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== onClose || $[15] !== onProfileStatementAdd || $[16] !== profile || $[17] !== setToast) {
    t7 = () => {
      setErrorMsg(null);
      setToast({
        message: "About statement added.",
        type: "success"
      });
      onProfileStatementAdd({
        profile
      });
      onClose();
    };
    $[14] = onClose;
    $[15] = onProfileStatementAdd;
    $[16] = profile;
    $[17] = setToast;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = error => {
      setErrorMsg(error as unknown as string);
    };
    t9 = () => {
      setLoading(false);
    };
    $[19] = t8;
    $[20] = t9;
  } else {
    t8 = $[19];
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t6 || $[22] !== t7) {
    t10 = {
      mutationFn: t6,
      onSuccess: t7,
      onError: t8,
      onSettled: t9
    };
    $[21] = t6;
    $[22] = t7;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const addStatementMutation = useMutation(t10);
  let t11;
  if ($[24] !== addStatementMutation || $[25] !== profileStatementTarget.length || $[26] !== requestAuth || $[27] !== value_2) {
    t11 = async () => {
      if (profileStatementTarget.length === 0 || value_2.trim().length === 0) {
        return;
      }
      const {
        success
      } = await requestAuth();
      if (!success) {
        return;
      }
      await addStatementMutation.mutateAsync(value_2);
    };
    $[24] = addStatementMutation;
    $[25] = profileStatementTarget.length;
    $[26] = requestAuth;
    $[27] = value_2;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const submitStatement = t11;
  let t12;
  if ($[29] !== submitStatement) {
    t12 = async event_0 => {
      event_0.preventDefault();
      await submitStatement();
    };
    $[29] = submitStatement;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const onSubmit = t12;
  let t13;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      resize: "none"
    };
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== value_2) {
    t14 = <textarea className="tw-form-input tw-block tw-min-h-28 tw-w-full tw-appearance-none tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-px-3.5 tw-py-3 tw-text-sm tw-font-medium tw-leading-6 tw-text-iron-50 tw-caret-primary-400 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-iron-700 tw-transition tw-duration-300 tw-ease-out placeholder:tw-text-iron-500 hover:tw-ring-iron-600 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400" name="profile-about" id="profile-about-input" aria-label="About statement" placeholder="Write an About statement" required={true} value={value_2} onChange={handleInputChange} ref={inputRef} style={t13} />;
    $[32] = value_2;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== value_2.length) {
    t15 = <div className="tw-text-xs tw-font-medium tw-text-iron-500">{value_2.length}/{500}</div>;
    $[34] = value_2.length;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== loading || $[37] !== onClose) {
    t16 = <SecondaryButton disabled={loading} onClicked={onClose}>Cancel</SecondaryButton>;
    $[36] = loading;
    $[37] = onClose;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== isDisabled || $[40] !== loading || $[41] !== submitStatement) {
    t17 = <PrimaryButton disabled={isDisabled} loading={loading} onClicked={submitStatement} hideChildrenWhenLoading={true}>Save</PrimaryButton>;
    $[39] = isDisabled;
    $[40] = loading;
    $[41] = submitStatement;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t16 || $[44] !== t17) {
    t18 = <div className="tw-flex tw-w-full tw-gap-2 sm:tw-w-auto">{t16}{t17}</div>;
    $[43] = t16;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t15 || $[47] !== t18) {
    t19 = <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">{t15}{t18}</div>;
    $[46] = t15;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== onSubmit || $[50] !== t14 || $[51] !== t19) {
    t20 = <form onSubmit={onSubmit}>{t14}{t19}</form>;
    $[49] = onSubmit;
    $[50] = t14;
    $[51] = t19;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== errorMsg) {
    t21 = errorMsg && <UserPageHeaderAboutEditError msg={errorMsg} closeError={() => setErrorMsg(null)} />;
    $[53] = errorMsg;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== t21) {
    t22 = <AnimatePresence mode="wait" initial={false}>{t21}</AnimatePresence>;
    $[55] = t21;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== t20 || $[58] !== t22) {
    t23 = <div className="tw-w-full tw-max-w-xl">{t20}{t22}</div>;
    $[57] = t20;
    $[58] = t22;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  return t23;
}
function _temp2(value_0) {
  return value_0.length > 0;
}
function _temp(value) {
  return value?.trim() ?? "";
}